import React, { useEffect } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { wrapLongitude } from '../../utils/coordinates';

/** Точка заведения на карте. */
export interface MapPoint {
  latitude: number;
  longitude: number;
}

interface PointPickerMapProps {
  /** Поставленная точка; `null` — точки ещё нет, маркера нет. */
  point: MapPoint | null;
  /** Куда смотрит карта: на точку, на найденный адрес или на город. */
  center: MapPoint;
  zoom: number;
  /** Сменился — карта переезжает на `center`. */
  focusKey: number;
  /** Клик по карте или перетащенный маркер. */
  onPick: (point: MapPoint) => void;
  height?: number;
  testId?: string;
}

const markerIcon = L.icon({
  iconUrl: process.env.PUBLIC_URL + '/marker-icon.svg',
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48],
  shadowUrl: undefined,
});

/**
 * Двигает карту, когда точку поставили не кликом: нашли по адресу, сменили
 * город. `focusKey` меняется — карта переезжает; на обычных перерисовках
 * формы она остаётся там, куда её увёл партнёр.
 */
function MapFocus({ center, zoom, focusKey }: { center: MapPoint; zoom: number; focusKey: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([center.latitude, center.longitude], zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);
  return null;
}

function PickOnClick({ onPick }: { onPick: (point: MapPoint) => void }) {
  useMapEvents({
    click(e: L.LeafletMouseEvent) {
      onPick({ latitude: e.latlng.lat, longitude: wrapLongitude(e.latlng.lng) });
    },
  });
  return null;
}

/**
 * Карта, на которой партнёр ставит точку заведения: кликом или
 * перетаскиванием маркера.
 *
 * Маркер можно перетащить: черновик по адресу часто стоит на улице, а не на
 * здании, и поправить его проще, чем кликать заново. Что делать с точкой —
 * сохранить, подставить адрес — решает форма через `onPick`.
 */
export function PointPickerMap({ point, center, zoom, focusKey, onPick, height = 320, testId }: PointPickerMapProps) {
  return (
    <div className="qp-map" style={{ height }} data-testid={testId}>
      <MapContainer center={[center.latitude, center.longitude]} zoom={zoom} style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapFocus center={center} zoom={zoom} focusKey={focusKey} />
        <PickOnClick onPick={onPick} />
        {point && (
          <Marker
            position={[point.latitude, point.longitude]}
            icon={markerIcon}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const moved = (e.target as L.Marker).getLatLng();
                onPick({ latitude: moved.lat, longitude: wrapLongitude(moved.lng) });
              },
            }}
          />
        )}
      </MapContainer>
    </div>
  );
}
